import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  SearchOutlined,
  EnvironmentOutlined,
  IdcardOutlined
} from '@ant-design/icons'
import { message, Radio, Space } from 'antd'

import debounce from 'lodash.debounce'

import FacilitiesFilters from 'components/FacilitiesFilters'
import ProvidersFilters from 'components/ProvidersFilters'

import LocationsAutocomplete from 'components/LocationsAutocomplete'
import InsuranceAutocomplete from 'components/InsuranceAutocomplete'
import ProviderNameSearch from 'components/ProviderNameSearch'
import FilterSystem from 'components/FilterSystem'
import { PROVIDER, FACILITY } from 'constants/constants'
import { IconContainer } from 'ui/entities/styled'
import { Button, Input, Modal } from 'ui/styled'
import EntitiesTable from './EntitiesTable'
import { Content, RadioWrap, TableWrap } from './styled'

const AttachEntityModal = ({
  onCancel,
  onSave,
  visible = false,
  memberId = '',
  forcedEntityType = undefined,
  isFacilityPage = false,
  addressUpdateLoading = false
}) => {
  const [filtersResetKey, setFiltersResetKey] = useState(Date.now())
  const [entityType, setEntityType] = useState(forcedEntityType || 'provider')
  const [planTiers, setPlanTiers] = useState()

  const isProvider = entityType === PROVIDER
  const isFacility = entityType === FACILITY

  const onSelect = entity => {
    onSave(entity)
    message.success('Selected!')
    !isFacilityPage && onCancel()
  }

  return (
    <Modal
      open={visible}
      padding="0"
      width="1200px"
      onCancel={onCancel}
      title={`Attach ${forcedEntityType || `${PROVIDER} or ${FACILITY}`}`}
      footer={null}
      data-test="attachEntityModal"
    >
      <Content>
        <RadioWrap>
          <Radio.Group
            value={entityType}
            onChange={e => setEntityType(e.target.value)}
          >
            <Radio value={PROVIDER} disabled={forcedEntityType === FACILITY}>
              Provider
            </Radio>
            <Radio value={FACILITY} disabled={forcedEntityType === PROVIDER}>
              Facility
            </Radio>
          </Radio.Group>
        </RadioWrap>
        <FilterSystem>
          {(
            filterValues,
            updateFilters,
            updateSeveralFilters,
            setFilters,
            resetFilters
          ) => {
            const onResetFilters = () => {
              setFiltersResetKey(Date.now())
              resetFilters()
            }

            const debouncedFiltersUpdate = debounce(updateFilters, 500)

            return (
              <div>
                <Space
                  orientation="vertical"
                  size="middle"
                  style={{ width: '100%' }}
                  key={filtersResetKey}
                >
                  <Space.Compact size="large" block>
                    {isFacility && (
                      <Input
                        placeholder="Facility Name"
                        onChange={e =>
                          debouncedFiltersUpdate('name', e.target.value)
                        }
                        defaultValue={filterValues.name}
                        data-test="attachFacilitySearch"
                        fontSize={16}
                      />
                    )}

                    {isProvider && (
                      <ProviderNameSearch
                        filterValues={filterValues}
                        filtersUpdateSeveral={val => updateSeveralFilters(val)}
                        onChange={debouncedFiltersUpdate}
                      />
                    )}

                    <IconContainer>
                      <EnvironmentOutlined />
                    </IconContainer>
                    <LocationsAutocomplete
                      memberId={memberId}
                      onSelect={v => updateFilters('address', v)}
                      onSearch={v => debouncedFiltersUpdate('address', v)}
                    />

                    {isProvider && (
                      <>
                        <IconContainer>
                          <IdcardOutlined />
                        </IconContainer>
                        <InsuranceAutocomplete
                          memberId={filterValues.member_id}
                          onChange={value =>
                            updateSeveralFilters([
                              { plan_id: value },
                              { insurance_plan_tiers: undefined }
                            ])
                          }
                          onPlanSelect={setPlanTiers}
                          defaultValue={filterValues.plan_id}
                        />
                      </>
                    )}

                    <Button btntype="primary" btnsize="large">
                      <SearchOutlined />
                    </Button>
                  </Space.Compact>

                  {isFacility && (
                    <FacilitiesFilters
                      filterValues={filterValues}
                      filtersUpdate={updateFilters}
                      filtersUpdateSeveral={updateSeveralFilters}
                      onResetFilters={onResetFilters}
                    />
                  )}

                  {isProvider && (
                    <ProvidersFilters
                      filterValues={filterValues}
                      planTiers={planTiers}
                      filtersUpdate={updateFilters}
                      filtersUpdateSeveral={updateSeveralFilters}
                      onResetFilters={onResetFilters}
                    />
                  )}
                </Space>
                <TableWrap>
                  <EntitiesTable
                    entityType={entityType}
                    filterValues={filterValues}
                    onSelect={onSelect}
                    addressUpdateLoading={addressUpdateLoading}
                    employee_id={memberId}
                  />
                </TableWrap>
              </div>
            )
          }}
        </FilterSystem>
      </Content>
    </Modal>
  )
}

AttachEntityModal.propTypes = {
  visible: PropTypes.bool,
  memberId: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  forcedEntityType: PropTypes.string,
  isFacilityPage: PropTypes.bool,
  addressUpdateLoading: PropTypes.bool
}

export default AttachEntityModal
